import * as React from "react"
import { useTranslation } from "react-i18next"
import { Icon, cn } from "@nexus/ui"

const PIPELINE_STEPS = [
  { icon: "users", labelKey: "personas.infoBanner.pipelineUserPersona" },
  { icon: "lightbulb", labelKey: "personas.infoBanner.pipelineOpportunity" },
  { icon: "file-text", labelKey: "personas.infoBanner.pipelineScript" },
] as const

export interface PersonasInfoBannerProps {
  open: boolean
  onClose: () => void
  className?: string
}

/** Inline, dismissible explainer for how user personas feed Opportunities/Briefs/Scripts. */
export function PersonasInfoBanner({ open, onClose, className }: PersonasInfoBannerProps) {
  const { t } = useTranslation()

  if (!open) return null

  return (
    <section
      aria-label={t("personas.infoBanner.title")}
      className={cn(
        "shrink-0 overflow-hidden rounded-lg border border-border/60 bg-card",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-300",
        className,
      )}
    >
      <div className="flex items-start gap-4 p-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-foreground">{t("personas.infoBanner.title")}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t("personas.infoBanner.description")}
          </p>
        </div>

        <ol className="hidden shrink-0 items-center gap-2 sm:flex" aria-label={t("personas.infoBanner.pipelineAria")}>
          {PIPELINE_STEPS.map((step, i) => (
            <React.Fragment key={step.labelKey}>
              {i > 0 && <Icon name="chevron-right" size="xs" aria-hidden className="text-muted-foreground/60" />}
              <li className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs font-medium text-foreground">
                <Icon name={step.icon} size="xs" className="text-muted-foreground" />
                {t(step.labelKey)}
              </li>
            </React.Fragment>
          ))}
        </ol>

        <button
          type="button"
          onClick={onClose}
          aria-label={t("personas.infoBanner.dismissAria")}
          className="-mr-1 -mt-1 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
        >
          <Icon name="x" size="xs" color="inherit" />
        </button>
      </div>
    </section>
  )
}
